import { Component, ElementRef, OnDestroy, OnInit, ViewChild } from "@angular/core";
import { FormControl, Validators } from "@angular/forms";
import { Subject, Subscription, finalize, switchMap, takeUntil, tap } from "rxjs";
import { AiSupportChatService } from "../../services/ai-support-chat/ai-support-chat.service";
import { AiSupportChatRequest, AiSupportMessage } from "@models/ai-support.model";
import { SupportChatApiService } from "../../services/support-chat/support-chat-api.service";
import { SupportSocketService } from "../../services/support-chat/support-socket.service";
import {
    SenderType,
    SpecialistStatus,
    StartSessionResponse,
    SupportMessage,
    SupportSessionStatus,
    SupportSpecialistSummary,
} from "@models/support-chat.model";
import { UserService } from "../../services/user/user.service";
import { AuthService } from "../../services/auth/auth.service";
import { NullableRegisteredUserDetails } from "@models/user.model";
import { SupportSessionAttributes } from "@models/supportSession.model";

@Component({
    selector: "app-ai-support-chat",
    templateUrl: "./ai-support-chat.component.html",
    styleUrls: ["./ai-support-chat.component.scss"],
    standalone: false,
})
export class AiSupportChatComponent implements OnInit, OnDestroy {
    @ViewChild("messageContainer") private messageContainer?: ElementRef<HTMLDivElement>;

    public isOpen: boolean = false;
    public isSending: boolean = false;
    public ticketId?: string;
    public isHumanChatActive: boolean = false;
    public isHumanRequestInFlight: boolean = false;
    public humanQueuePosition?: number | null;
    public assignedSpecialist?: SupportSpecialistSummary | null;
    public currentSessionPublicId?: string;
    private hasShownQueueMessage = false;
    private userDetails?: NullableRegisteredUserDetails;
    private session!: SupportSessionAttributes;

    public messages: AiSupportMessage[] = [
        {
            sender: SenderType.Assistant,
            content: "Hi! I'm the FunFigz assistant. Ask me anything about your orders, designs, or account.",
            timestamp: new Date().toISOString(),
        },
    ];

    public messageControl = new FormControl("", {
        nonNullable: true,
        validators: [Validators.required, Validators.minLength(2)],
    });

    private destroy$ = new Subject<void>();
    private socketSubscriptions = new Subscription();

    constructor(
        private readonly aiSupportChatService: AiSupportChatService,
        private readonly supportChatApiService: SupportChatApiService,
        private readonly supportSocketService: SupportSocketService,
        private userService: UserService,
        private authService: AuthService,
    ) {}

    ngOnInit(): void {
        this.registerSocketStreams();

        this.authService.isLoggedIn$.subscribe((loginStatus) => {
            if (loginStatus) {
                this.userService
                    .getUserDetails()
                    .pipe(
                        tap((response) => (this.userDetails = response)),
                        switchMap(() => this.loadSessionMessages()), // waits for session load
                    )
                    .subscribe(() => {
                        if (this.currentSessionPublicId) {
                            this.reconnectToSocket(this.currentSessionPublicId);
                        }
                    });
            } else {
                this.resetHumanState();
                this.userDetails = undefined;
                this.messages = [
                    {
                        sender: SenderType.Assistant,
                        content:
                            "Hi! I'm the FunFigz assistant. Ask me anything about your orders, designs, or account.",
                        timestamp: new Date().toISOString(),
                    },
                ];
            }
            this.isOpen = false;
        });
    }

    public toggleChat(): void {
        this.isOpen = !this.isOpen;
        if (this.isOpen) {
            // Use setTimeout to ensure DOM is rendered before scrolling
            setTimeout(() => {
                this.scrollToBottom();
            }, 100);
        }
    }

    public close(): void {
        this.isOpen = false;
    }

    public handleEnter(event: KeyboardEvent): void {
        if (event.key === "Enter" && !event.shiftKey) {
            event.preventDefault();
            this.sendMessage();
        }
    }

    public sendMessage(): void {
        const trimmedValue = this.messageControl.value.trim();
        if (!trimmedValue || this.isSending) {
            return;
        }

        if (!this.currentSessionPublicId && this.session && this.session.status != SupportSessionStatus.Closed) {
            this.messages = [];
        }

        // If a session exists (active or queued), send directly to specialist without AI processing
        if (this.currentSessionPublicId) {
            // Add message optimistically so user sees it immediately
            const userMessage = this.buildMessage(SenderType.User, trimmedValue);
            this.messages = [...this.messages, userMessage];
            this.messageControl.setValue("");
            this.scrollToBottom();
            this.humanQueuePosition = null;
            this.ticketId = undefined;
            this.isSending = true;
            this.supportSocketService
                .sendUserMessage(this.currentSessionPublicId, trimmedValue)
                .then(() => {
                    this.isSending = false;
                    // Message already added optimistically, socket will sync if needed
                })
                .catch(() => {
                    this.isSending = false;
                    this.handleFailure();
                });
            return;
        }

        // AI processing only for the FIRST message when no session exists
        // After AI response, we'll connect to specialist
        const userMessage = this.buildMessage(SenderType.User, trimmedValue);
        this.messages = [...this.messages, userMessage];
        this.messageControl.setValue("");
        this.scrollToBottom();

        this.isSending = true;
        const payload: AiSupportChatRequest = {
            topic: "funfigz-support",
            messages: this.messages.map((message) => ({ ...message })),
        };

        this.aiSupportChatService
            .sendMessage(payload)
            .pipe(
                takeUntil(this.destroy$),
                finalize(() => {
                    this.isSending = false;
                }),
            )
            .subscribe({
                next: (response) => {
                    this.messages = [...this.messages, response.reply];
                    this.ticketId = response.ticketId;
                    // After first AI response, connect to specialist (if not already connected)
                    if (!this.currentSessionPublicId && !this.isHumanRequestInFlight && !this.isHumanChatActive) {
                        this.connectToAdmin();
                    }

                    this.scrollToBottom();
                },
                error: () => {
                    this.handleFailure();
                    // On error, also try to connect to specialist
                    if (!this.currentSessionPublicId && !this.isHumanRequestInFlight && !this.isHumanChatActive) {
                        this.connectToAdmin();
                    }
                },
            });
    }

    private connectToAdmin(): void {
        if (this.isHumanRequestInFlight) {
            return;
        }

        this.isHumanRequestInFlight = true;
        // Store AI conversation history before connecting
        const aiHistory = this.messages
            .filter((m) => m.sender === "user" || m.sender === "assistant")
            .map((m) => ({
                sender: m.sender === "assistant" ? "assistant" : "user",
                content: m.content,
                timestamp: m.timestamp,
            }));

        this.supportSocketService
            .connectUser({
                displayName: this.userDetails?.user.name,
                contactEmail: this.userDetails?.user.email,
                contactPhone: this.userDetails?.mobileNumber,
                topic: `AI Chat History: ${aiHistory.length} messages`,
                aiHistory: JSON.stringify(aiHistory),
                senderId: this.userDetails?.user.id,
            })
            .then((response: StartSessionResponse) => {
                this.session = response.session!;
                this.isHumanRequestInFlight = false;
                // Always set session ID - this ensures all future messages go to specialist
                this.currentSessionPublicId = response.session.publicId;
                this.assignedSpecialist = response.specialist ?? undefined;
                this.isHumanChatActive = !response.queued;
                this.humanQueuePosition = response.session.queuePosition ?? null;
                // Only show queue message on first connection, not on subsequent replies
                if (response.queued && !this.hasShownQueueMessage) {
                    this.pushSystemMessage("All specialists are busy right now. You're placed in the queue.");
                    this.hasShownQueueMessage = true;
                }
                // Clear ticket ID when connected
                this.ticketId = undefined;
            })
            .catch((error: Error) => {
                this.isHumanRequestInFlight = false;
                this.pushSystemMessage(error.message || "Unable to connect to a specialist right now.");
            });
    }

    private scrollToBottom(): void {
        // Use requestAnimationFrame and setTimeout for reliable scrolling
        requestAnimationFrame(() => {
            setTimeout(() => {
                const container = this.messageContainer?.nativeElement;
                if (container) {
                    container.scrollTop = container.scrollHeight;
                }
            }, 0);
        });
    }

    private registerSocketStreams(): void {
        this.socketSubscriptions.add(
            this.supportSocketService.onMessage().subscribe(({ message }: { message: SupportMessage }) => {
                // Only add messages that don't already exist to avoid duplicates
                // Check by content and approximate timestamp (within 5 seconds) to handle timing differences
                const messageTime = new Date(message.createdAt).getTime();
                const exists = this.messages.some((m) => {
                    if (!m.timestamp) {
                        return false;
                    }
                    const existingTime = new Date(m.timestamp).getTime();
                    return (
                        m.content === message.body && Math.abs(messageTime - existingTime) < 5000 // 5 second window
                    );
                });
                if (exists) {
                    return;
                }
                const sender: SenderType =
                    message.senderType === SenderType.System
                        ? SenderType.System
                        : message.senderType === SenderType.Specialist
                        ? SenderType.Assistant
                        : SenderType.User;
                this.messages = [
                    ...this.messages,
                    {
                        sender,
                        content: message.body,
                        timestamp: message.createdAt,
                    },
                ];
                // Use setTimeout to ensure DOM is updated before scrolling
                setTimeout(() => {
                    this.scrollToBottom();
                }, 100);
            }),
        );

        this.socketSubscriptions.add(
            this.supportSocketService.onSessionUpdate().subscribe((update: any) => {
                if (!update) {
                    return;
                }
                console.log("Received session update:", update);
                if (update.status === "ACTIVE") {
                    this.isHumanChatActive = true;
                    this.humanQueuePosition = null;
                    this.ticketId = undefined; // Clear ticket ID when connected
                    // Ensure session ID is set
                    if (update.sessionId && !this.currentSessionPublicId) {
                        this.currentSessionPublicId = update.sessionId;
                    }
                    // Load session messages to sync chat history
                    if (this.currentSessionPublicId) {
                        this.loadSessionMessages(this.currentSessionPublicId);
                    }
                    // Update specialist info if provided
                    if (update.specialist) {
                        this.assignedSpecialist = {
                            id: update.specialist.id,
                            name: update.specialist.name,
                            email: update.specialist.email,
                            status: SpecialistStatus.Busy,
                        };
                        this.pushSystemMessage(
                            `You are now connected with ${update.specialist.name || "a specialist"}.`,
                        );
                    }
                } else if (update.status === "QUEUED") {
                    this.humanQueuePosition = update.queuePosition ?? null;
                    // Ensure session ID is set even when queued
                    if (update.sessionId && !this.currentSessionPublicId) {
                        this.currentSessionPublicId = update.sessionId;
                    }
                } else if (update.status === "CLOSED") {
                    this.pushSystemMessage("You are now disconnected.");
                    this.resetHumanState();
                }
            }),
        );
    }

    private loadSessionMessages(publicId?: string) {
        return this.supportChatApiService.getSession(publicId!).pipe(
            tap((session) => {
                this.session = session;
                if (session && session.messages && session.messages.length > 0) {
                    if (!this.currentSessionPublicId) {
                        this.currentSessionPublicId = session.publicId;
                    }
                    // Merge session messages with current messages, avoiding duplicates
                    const existingTimestamps = new Set(this.messages.map((m) => m.timestamp).filter(Boolean));
                    const newMessages = session.messages
                        .filter((msg) => !existingTimestamps.has(msg.createdAt))
                        .map((msg) => {
                            const sender: SenderType =
                                msg.senderType === SenderType.System
                                    ? SenderType.System
                                    : msg.senderType === SenderType.Specialist
                                    ? SenderType.Assistant
                                    : SenderType.User;
                            return {
                                sender,
                                content: msg.body,
                                timestamp: msg.createdAt,
                            };
                        });
                    if (newMessages.length > 0) {
                        this.messages = [...this.messages, ...newMessages];
                        this.scrollToBottom();
                    } else {
                        // Even if no new messages, scroll to bottom to ensure we're at the latest
                        this.scrollToBottom();
                    }
                }
            }),
        );
    }

    private resetHumanState(): void {
        this.isHumanChatActive = false;
        this.humanQueuePosition = null;
        this.assignedSpecialist = undefined;
        this.currentSessionPublicId = undefined;
        this.hasShownQueueMessage = false;
        this.supportSocketService.disconnect();
    }

    private handleFailure(): void {
        this.pushSystemMessage("I couldn't complete that request. A specialist will assist you shortly.");
    }

    private pushSystemMessage(content: string): void {
        const message = this.buildMessage(SenderType.System, content);
        this.messages = [...this.messages, message];
        this.scrollToBottom();
    }

    private buildMessage(sender: AiSupportMessage["sender"], content: string): AiSupportMessage {
        return {
            sender,
            content,
            timestamp: new Date().toISOString(),
        };
    }

    private reconnectToSocket(currentSessionPublicId: string): void {
        this.supportSocketService.userJoinSession(currentSessionPublicId);
    }

    public ngOnDestroy(): void {
        this.destroy$.next();
        this.destroy$.complete();
        this.socketSubscriptions.unsubscribe();
        this.supportSocketService.disconnect();
    }
}
